'use client';

import { BasePrescriptionRecognition } from '@/app/[variants]/_lens-components/types';
import Button from '@/components/Button';
import Image from '@/components/Image';
import PopupResponsive from '@/components/PopupResponsive';
import { env } from '@/utils/env';
import { useMediaQuery } from '@mui/material';
import { CheckOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useState } from 'react';
import { PhotoProvider, PhotoView } from 'react-photo-view';

interface ResultDialogProps {
  isOpen: boolean;
  onClose: () => void;
  resultData: BasePrescriptionRecognition | null;
  uploadedS3Url: string;
  onCorrectionConfirm: (selectedOption: { value: string; type: string }) => void;
}

export default function ResultDialog({
  isOpen,
  onClose,
  resultData,
  uploadedS3Url,
  onCorrectionConfirm,
}: ResultDialogProps) {
  const t = useTranslations('lens-process');
  const isWap = useMediaQuery('(max-width:1280px)');

  // 纠错选项相关状态
  const [currentChecked, setCurrentChecked] = useState('');
  const [correctionOptions, setCorrectionOptions] = useState<
    Array<{
      label: string;
      value: string;
      type: 'changePres' | 'ignoreAdd';
    }>
  >([]);

  // 处理纠错选项
  useEffect(() => {
    if (resultData && resultData.prescription_correction) {
      const options: Array<{
        label: string;
        value: string;
        type: 'changePres' | 'ignoreAdd';
      }> = [];

      const { correction_list } = resultData.prescription_correction;

      if (correction_list) {
        // 处理支持的处方类型
        if (correction_list.support && Array.isArray(correction_list.support)) {
          correction_list.support.forEach((item: string) => {
            const prescriptionsTextMap = {
              'Reading Glasses': t('readingGlasses'),
              Progressive: t('progressive'),
              'Single Vision': t('singleVision'),
              'Non-Prescription': t('nonPrescription'),
            };
            const text = prescriptionsTextMap[item];
            if (item === 'Reading Glasses') {
              options.push({
                label: t('switchToReadingGlasses', { item: text }),
                value: item,
                type: 'changePres',
              });
            } else {
              options.push({
                label: t('switchToPrescription', { item: text }),
                value: item,
                type: 'changePres',
              });
            }
          });
        }

        // 处理忽略的ADD值
        if (correction_list.ignore) {
          Object.keys(correction_list.ignore).forEach((key) => {
            options.push({
              label: t('proceedWithout', { key: key.toString().toUpperCase() }),
              value: key,
              type: 'ignoreAdd',
            });
          });
        }
      }

      setCorrectionOptions(options);

      // 如果只有一个ignoreAdd选项，默认选中
      if (options.length === 1 && options[0].type === 'ignoreAdd') {
        setCurrentChecked(options[0].value);
      } else {
        setCurrentChecked('');
      }
    }
  }, [resultData, t]);

  // 处理选项选择
  const handleOptionSelect = (option: { value: string; type: string }) => {
    if (option.value === currentChecked) {
      setCurrentChecked('');
    } else {
      setCurrentChecked(option.value);
    }
  };

  // 处理纠错弹窗确认
  const handleConfirm = () => {
    if (!currentChecked) return;

    const selectedOption = correctionOptions.find((option) => option.value === currentChecked);
    if (selectedOption) {
      onCorrectionConfirm(selectedOption);
    }
  };

  return (
    <PopupResponsive
      open={isOpen}
      onClose={onClose}
      position="bottom"
      bodyClassName="!p-0"
      closeOnClickOverlay={false}
      showDivider={false}
      height="auto"
      title={t('yourScanResults')}
    >
      <div className="max-h-[60vh] min-h-[52vh] overflow-y-auto px-[14px] pb-[40px]">
        {resultData && (
          <>
            {/* 提示信息 */}
            <div className="w-full bg-[#f8e8e8] rounded-[6px] p-[9px_8px] flex items-baseline">
              <Image
                src="/lens-process/warning.png"
                width={13}
                height={12}
                alt={t('warning')}
                className="translate-y-[2px]"
              />
              <p className="text-[#bc1b1b] text-[12px] leading-[18px] ml-[6px]">
                {resultData.prescription_correction?.message || ''}
              </p>
            </div>

            {/* 处方图片 */}
            <div className="mt-[12px] w-full overflow-hidden rounded-[8px] bg-[grey] flex items-center justify-center">
              <PhotoProvider maskClassName="!bg-black/50" bannerVisible={false}>
                <PhotoView src={env.NEXT_PUBLIC_S3_URL + uploadedS3Url}>
                  <Image
                    src={env.NEXT_PUBLIC_S3_URL + uploadedS3Url}
                    width={400}
                    height={300}
                    alt={t('prescription')}
                    className="max-w-full max-h-[50vh] w-[auto] h-[auto] cursor-pointer"
                  />
                </PhotoView>
              </PhotoProvider>
            </div>

            {/* 处方类型选择 - 只在纠错时显示 */}
            {resultData.prescription_correction?.type === 5 && correctionOptions.length > 0 && (
              <div className="mt-[32px]">
                <p className="text-[16px] leading-[24px] text-primary-black font-bs">
                  {t('whatTypeOfPrescriptionWouldYouLikeToContinueUsing')}
                </p>
                <ul className="mt-[16px]">
                  {correctionOptions.map((option, index) => (
                    <li
                      key={index}
                      className={`mt-[8px] first:mt-0 h-[46px] px-[12px] rounded-[6px] bg-[#f5f6f7] flex items-center cursor-pointer ${isWap ? 'justify-between' : ''}`}
                      onClick={() => handleOptionSelect(option)}
                    >
                      {isWap && <span className="f12">{option.label}</span>}
                      <div
                        className={`w-[16px] h-[16px] border ${
                          currentChecked === option.value
                            ? 'bg-primary-purple border-primary-purple'
                            : 'bg-white border-gray-300'
                        } rounded-[4px] flex items-center justify-center`}
                      >
                        {currentChecked === option.value && (
                          <CheckOutlined className="text-white" />
                        )}
                      </div>
                      {!isWap && <span className="ml-[10px] f12">{option.label}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {/* 底部按钮 */}
      <div className="h-[69px] shadow-[0px_-1px_5px_0px_rgba(204,204,204,0.5)] flex items-center justify-center">
        {resultData?.prescription_correction?.type === 3 ? (
          <Button
            variant="contained"
            className="w-[285px] h-[40px] !font-bs rounded-[20px] !text-white"
            onClick={onClose}
          >
            {t('back')}
          </Button>
        ) : (
          <Button
            variant="contained"
            className="w-[285px] h-[40px] !font-bs rounded-[20px] !text-white"
            disabled={currentChecked === ''}
            onClick={handleConfirm}
          >
            {t('next')}
          </Button>
        )}
      </div>
    </PopupResponsive>
  );
}
